import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { GET_BRANDS, type IGetBrands, type IGetBrandsVariables } from '@commerce/core'

/** Same ceiling the category rail uses: one page, because a filter list is short. */
const BRAND_PAGE_SIZE = 100

export interface UseBrandOptions {
  brands: string[]
  loading: boolean
}

/**
 * The brands a filter sheet offers.
 *
 * `skip` rather than a lazy query: the sheet is what asks, and it asks by being
 * open. A `useLazyQuery` here would need the caller to remember to fire it, and
 * firing it twice is a second round trip for a list that does not change while
 * the shopper browses.
 *
 * Names are deduped and sorted alphabetically — `brands` is authored data and
 * arrives in insertion order, which is meaningless to someone scanning a list.
 */
export const useBrandOptions = (enabled: boolean): UseBrandOptions => {
  const { slug = '' } = useParams()

  const { data, loading } = useQuery<IGetBrands, IGetBrandsVariables>(GET_BRANDS, {
    variables: {
      storefrontName: slug,
      pagination: { pageNumber: 1, pageSize: BRAND_PAGE_SIZE },
    },
    skip: !enabled,
  })

  const brands = useMemo(() => {
    const names = (data?.brands ?? [])
      .map((brand) => brand?.name)
      .filter((name): name is string => typeof name === 'string' && name.trim() !== '')
    return [...new Set(names)].sort((left, right) => left.localeCompare(right))
  }, [data])

  return { brands, loading }
}
